import React, { useState, useEffect, FormEvent } from 'react';
import { collection, query, where, orderBy, getDocs, addDoc, updateDoc, doc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { Star, StarHalf, User } from 'lucide-react';
import { toast } from 'sonner';

interface Review {
  id: string;
  productId: string;
  userId: string;
  userName: string;
  rating: number;
  comment: string;
  createdAt: any;
}

export function ProductReviews({ productId, vendorId }: { productId: string, vendorId?: string }) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [ratingInput, setRatingInput] = useState(5);
  const [commentInput, setCommentInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [userHasReviewed, setUserHasReviewed] = useState(false);

  const currentUser = auth.currentUser;
  const isOwner = currentUser?.uid === vendorId;

  const fetchReviews = async () => {
    try {
      const q = query(
        collection(db, 'product_reviews'),
        where('productId', '==', productId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const fetchedReviews = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Review[];
      
      setReviews(fetchedReviews);
      
      if (currentUser) {
        const hasReviewed = fetchedReviews.some(r => r.userId === currentUser.uid);
        setUserHasReviewed(hasReviewed);
      }
    } catch (error) {
      console.error("Failed to fetch reviews", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [productId, currentUser]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!currentUser) {
      toast.error("You must be signed in to leave a review.");
      return;
    }
    if (isOwner) {
      toast.error("Vendors cannot review their own products.");
      return;
    }

    setSubmitting(true);
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userName = userSnap.exists() ? (userSnap.data().displayName || userSnap.data().email.split('@')[0]) : 'User';

      const newReview = {
        productId,
        userId: currentUser.uid,
        userName,
        rating: ratingInput,
        comment: commentInput,
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, 'product_reviews'), newReview);
      
      // Update the average rating on the product
      const currentRatingSum = reviews.reduce((sum, r) => sum + r.rating, 0) + ratingInput;
      const newRatingCount = reviews.length + 1;
      const newAverageRating = currentRatingSum / newRatingCount;

      await updateDoc(doc(db, 'products', productId), {
        ratingAverage: newAverageRating,
        ratingCount: newRatingCount
      });

      toast.success("Review submitted successfully!");
      setCommentInput('');
      setRatingInput(5);
      fetchReviews();
    } catch (error: any) {
      toast.error("Failed to submit review");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ rating }: { rating: number }) => {
    return (
      <div className="flex text-yellow-400">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star}>
            {star <= rating ? (
              <Star className="w-4 h-4 fill-current" />
            ) : (
              <Star className="w-4 h-4 text-neutral-300 dark:text-neutral-600" />
            )}
          </span>
        ))}
      </div>
    );
  };

  if (loading) {
    return <div className="animate-pulse h-20 bg-neutral-100 dark:bg-neutral-800 rounded-xl" />;
  }

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : '0.0';

  return (
    <div className="mt-12 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-6 sm:p-8">
      <h3 className="text-2xl font-serif font-medium text-neutral-900 dark:text-white mb-6">Customer Reviews</h3>
      
      {/* Summary */}
      <div className="flex items-center gap-4 mb-8">
        <div className="text-4xl font-bold text-neutral-900 dark:text-white">
          {Number(averageRating) > 0 ? averageRating : '0.0'}
        </div>
        <div>
          {reviews.length > 0 ? <StarRating rating={Number(averageRating)} /> : <div className="text-sm text-neutral-500">No rating yet</div>}
          <div className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Based on {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
          </div>
        </div>
      </div>

      {/* Review Form */}
      {currentUser && !isOwner && !userHasReviewed ? (
        <form onSubmit={handleSubmit} className="mb-10 bg-neutral-50 dark:bg-neutral-900/50 p-6 rounded-xl border border-neutral-200 dark:border-neutral-800">
          <h4 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">Write a Review</h4>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Rating</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRatingInput(star)}
                  className="focus:outline-none transition-transform hover:scale-110"
                >
                  <Star className={`w-8 h-8 ${star <= ratingInput ? 'fill-yellow-400 text-yellow-400' : 'text-neutral-300 dark:text-neutral-600'}`} />
                </button>
              ))}
            </div>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Comment</label>
            <textarea
              required
              value={commentInput}
              onChange={(e) => setCommentInput(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500 resize-none outline-none transition-colors"
              placeholder="What did you like or dislike?"
            />
          </div>
          
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2.5 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 rounded-xl font-medium hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit Review'}
          </button>
        </form>
      ) : currentUser && isOwner ? (
        <div className="mb-8 p-4 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 rounded-lg text-sm border border-amber-200 dark:border-amber-900/50">
          Vendors cannot rate or review their own products.
        </div>
      ) : currentUser && userHasReviewed ? (
        <div className="mb-8 p-4 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200 rounded-lg text-sm border border-green-200 dark:border-green-900/50">
          You have already reviewed this product.
        </div>
      ) : (
        <div className="mb-8 text-neutral-500 dark:text-neutral-400 text-sm">
          Please sign in to write a review.
        </div>
      )}

      {/* Review List */}
      <div className="space-y-6">
        {reviews.length > 0 ? (
          reviews.map((review) => (
            <div key={review.id} className="border-b border-neutral-100 dark:border-neutral-800 pb-6 last:border-0 last:pb-0">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-500">
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-medium text-neutral-900 dark:text-white">{review.userName}</div>
                  <StarRating rating={review.rating} />
                </div>
                <div className="ml-auto text-xs text-neutral-400 dark:text-neutral-500">
                  {review.createdAt ? new Date(review.createdAt.toDate()).toLocaleDateString() : 'Just now'}
                </div>
              </div>
              <p className="text-neutral-600 dark:text-neutral-400 mt-3 text-sm leading-relaxed">
                {review.comment}
              </p>
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-neutral-500 dark:text-neutral-400 border border-dashed border-neutral-200 dark:border-neutral-800 rounded-xl">
            No reviews yet. Be the first to review this product!
          </div>
        )}
      </div>
    </div>
  );
}
